import Vue from 'vue'
import { i18n } from '~src/i18n'
import _ from 'lodash'
import {
  getProjectsQuery,
  getProjectByIdQuery,
  createProjectMutation,
  updateProjectMutation,
  deleteProjectMutation
} from '~src/graphql/projects.graphql'
import {
  getProjectStagesQuery,
  createProjectStageMutation,
  deleteProjectStageMutation,
  updateProjectStageMutation
} from '~graphql/projectStages.graphql'
import {
  getPartnersQuery,
  createPartnerMutation,
  updatePartnerMutation,
  getPartnerByIdQuery,
  softDeletePartnerMutation
} from '~graphql/partners.graphql'
import { getProjectPartnersByProjectIdQuery } from '~graphql/projectPartners.graphql'
import restApi from '~services/restApi'

export default {
  getAllProjects({ state, dispatch }) {
    return dispatch('getItems', {
      state,
      loader: 'projects:get',
      modelName: 'projects',
      query: getProjectsQuery,
      resultPath: 'projects.nodes'
    })
  },
  getProjectById({ state, dispatch }, id) {
    return dispatch('getItemById', {
      id,
      state,
      loader: 'project:get',
      modelName: 'selectedProject',
      addTo: ['projects'],
      query: getProjectByIdQuery,
      resultPath: 'project'
    })
  },
  getAllProjectDataById({ dispatch }, projectId) {
    Vue.loadingDialog({ text: i18n.t('main_module.loading_workspace_data') })

    // if (!Vue.roleAdminAndUp) {
    //   const me = rootState.authModule.me
    //   const userProfile = me.userProfiles?.nodes?.find(profile => profile.projectId === projectId)
    //   const selectedProjectPermissions = userProfile?.profile.permissions

    //   // Parse enum to UI
    //   const parsedPermissions = selectedProjectPermissions.map(permission => {
    //     return permission.replace(/_/g, '.').toLowerCase()
    //   })
    //   parsedPermissions.forEach(permission => {
    //     const permissionParts = permission.split('.')
    //     if (!parsedPermissions.includes(permissionParts[0])) {
    //       parsedPermissions.push(permissionParts[0])
    //     }
    //   })

    //   commit('SET_ITEM', {
    //     item: parsedPermissions,
    //     state: state,
    //     modelName: 'selectedProjectPermissions'
    //   })
    // }

    dispatch('clearGeometries')
    dispatch('clearLands')
    dispatch('clearResidents')
    dispatch('clearLandResidents')
    dispatch('clearLayers')
    const promises = []
    promises.push(dispatch('getProjectById', projectId))

    return Promise.all(promises).finally(() => {
      Vue.closeLoadingDialog()
    })
  },
  createProject({ dispatch, state }, project) {
    dispatch('startLoading', 'project:create:full')

    return dispatch('createItem', {
      item: project,
      state,
      addTo: ['projects'],
      modelName: 'selectedProject',
      loader: 'project:persist',
      mutation: createProjectMutation,
      resultPath: 'createProject.project'
    }).then(project => {
      dispatch('endLoading', 'project:create:full')
      return project
    })
  },
  updateProject({ dispatch, state }, projectPatch) {
    return dispatch('updateItem', {
      item: projectPatch,
      state,
      modelName: 'selectedProject',
      addTo: ['projects'],
      loader: 'project:persist',
      mutation: updateProjectMutation,
      resultPath: 'updateProject.project'
    })
  },
  deleteProject({ dispatch, state }, payload) {
    return dispatch('updateItem', {
      item: payload,
      state,
      modelName: 'selectedProject',
      removeFrom: ['projects'],
      loader: 'project:delete',
      mutation: deleteProjectMutation,
      resultPath: 'updateProject.project'
    })
  },
  setSelectedProject({ state, commit }, project) {
    commit('SET_ITEM', {
      item: project,
      state,
      modelName: 'selectedProject'
    })
  },

  clearSelectedProject({ commit, state }) {
    commit('SET_ITEM', {
      item: {},
      state,
      modelName: 'selectedProject'
    })
  },

  // Project Stages

  getAllProjectStages({ state, dispatch }) {
    return dispatch('getItems', {
      state,
      useCache: true,
      loader: 'projectStages:get',
      modelName: 'projectStages',
      query: getProjectStagesQuery,
      resultPath: 'projectStages.nodes'
    })
  },
  createProjectStage({ state, dispatch }, projectStage) {
    return dispatch('createItem', {
      item: { projectStage },
      state,
      loader: 'projectStage:create',
      addTo: ['projectStages'],
      mutation: createProjectStageMutation,
      resultPath: 'createProjectStage.projectStage'
    })
  },
  updateProjectStage({ state, dispatch }, payload) {
    return dispatch('updateItem', {
      item: payload,
      state,
      modelName: 'selectedProjectStage',
      addTo: ['projectStages'],
      loader: 'projectStage:update',
      mutation: updateProjectStageMutation,
      resultPath: 'updateProjectStage.projectStage'
    })
  },
  deleteProjectStage({ state, dispatch }, id) {
    return dispatch('deleteItem', {
      id,
      state,
      modelName: 'selectedProjectStage',
      removeFrom: ['projectStages'],
      loader: 'projectStage:delete',
      mutation: deleteProjectStageMutation
    })
  },
  setSelectedProjectStage({ state, commit }, selectedProjectStage) {
    commit('SET_ITEM', {
      state,
      item: selectedProjectStage,
      modelName: 'selectedProjectStage'
    })
  },
  // Get all partners
  getPartners({ state, dispatch, commit }, { filter, offset, first, orderBy }) {
    const defaultFilter = {
      id: {
        isNull: false
      } // used to allow empty filter
    }
    const mergedFilter = _.merge(filter, defaultFilter)

    commit('SET_ITEM', {
      state,
      item: [],
      modelName: 'partners'
    })

    return dispatch('getItems', {
      state,
      modelName: 'partners',
      loader: 'partners:get',
      query: getPartnersQuery, // GraphQL query to fetch all partners
      resultPath: 'partners.nodes', // Path to result in GraphQL response
      totalPath: 'partners.totalCount',
      totalModelName: 'partnersTotalCount',
      variables: {
        filter: mergedFilter,
        offset,
        first,
        orderBy
      }
    })
  },

  getPartnerById({ dispatch, state }, id) {
    return dispatch('getItemById', {
      id,
      state,
      loader: 'partner:get',
      modelName: 'selectedPartner',
      query: getPartnerByIdQuery,
      resultPath: 'partner',
      useCache: false
    })
  },
  // Create a new partner
  createPartner({ state, dispatch }, partner) {
    return dispatch('createItem', {
      item: { partner },
      state,
      addTo: ['partners'],
      modelName: 'selectedPartner',
      loader: 'partner:persist',
      mutation: createPartnerMutation, // GraphQL mutation to create a partner
      resultPath: 'createPartner.partner' // Path to result in GraphQL response
    })
  },

  // Update an existing partner
  updatePartner({ state, dispatch }, partner) {
    return dispatch('updateItem', {
      item: partner,
      state,
      addTo: ['partners'],
      modelName: 'selectedPartner',
      loader: 'partner:persist',
      mutation: updatePartnerMutation, // GraphQL mutation to update a partner
      resultPath: 'updatePartner.partner' // Path to result in GraphQL response
    })
  },

  // Delete a partner by ID
  deletePartner({ state, dispatch }, payload) {
    return dispatch('updateItem', {
      item: payload,
      state,
      removeFrom: ['partners'],
      loader: 'partner:delete',
      mutation: softDeletePartnerMutation,
      resultPath: 'updatePartner.partner'
    })
  },

  // Set selected partner
  setSelectedPartner({ state, commit }, partner) {
    commit('SET_ITEM', {
      state,
      item: partner,
      modelName: 'selectedPartner'
    })
  },

  // Get all project partners
  getProjectPartnersByProjectId({ state, dispatch }, projectId) {
    return dispatch('getItems', {
      state,
      modelName: 'projectPartners',
      loader: 'projectPartners:getAll',
      query: getProjectPartnersByProjectIdQuery,
      resultPath: 'projectPartners.nodes',
      variables: { projectId }
    })
  }
}
